import { memo, useEffect, useMemo } from 'react';
import { Resizable, Size } from 're-resizable';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import styled from 'styled-components';
import { useSetRecoilState } from 'recoil';

import { useResizableWithPersistence, type UseResizableProps } from 'hooks/useResizable';
import { reporterRightDrawerState } from '../state';
import { logger } from 'modules/logger';
import { Colors } from 'styles';
import { FIELD_BUILDER_PANE_COLLAPSED_WIDTH } from '../FieldBuilderPane/constants';
import { useOptionalRightDrawerDispatch } from './useRightDrawer';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

interface ResizeHandleProps {
  showCollapseWarning: boolean;
  collapsed: boolean;
}

const ResizeHandle = styled.div.attrs({ tabIndex: 0 })<ResizeHandleProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 10px;

  &:hover,
  &:active {
    &::before {
      border-left-color: ${(props) => (props.showCollapseWarning ? Colors.red4 : Colors.gray5)};
    }
  }
  &:focus {
    outline: 0;
  }

  &::before {
    content: '';
    height: 100%;
    transition: border-color 0.2s ease-in-out;
    border-left: ${(props) => (props.collapsed ? 8 : 2)}px solid
      ${(props) => (props.collapsed ? Colors.gray5 : Colors.gray4)};
  }
`;

const COLLAPSE_PANEL_WIDTH = 280;
const MIN_PANEL_WIDTH = 0;
const MAX_PANEL_WIDTH = 700;
const resizableEnable = { left: true } as const;

type RightDrawerType = 'RightDrawer' | 'FieldBuilderPane';

type RightDrawerWrapperProps = {
  children: React.ReactNode;
  initialSize?: number;
  type?: RightDrawerType;
  maxWidth?: number;
  collapseSize?: number;
  onHeightChange?: (height: number) => void;
  loggerLabel?: string;
};

export const RightDrawerWrapper = memo(
  ({
    children,
    initialSize,
    type = 'RightDrawer',
    maxWidth,
    collapseSize,
    onHeightChange,
    loggerLabel,
  }: RightDrawerWrapperProps): React.ReactElement => {
    const setReporterPreferences = useSetRecoilState(reporterRightDrawerState);
    const setRightDrawer = useOptionalRightDrawerDispatch();

    const resolvedCollapseSize =
      collapseSize ??
      (type === 'RightDrawer' ? COLLAPSE_PANEL_WIDTH : FIELD_BUILDER_PANE_COLLAPSED_WIDTH);
    const resolvedLoggerLabel = loggerLabel ?? type;
    const resolvedInitialSize = initialSize ?? resolvedCollapseSize;

    const sidebarResizeOptions: UseResizableProps = {
      collapseSize: resolvedCollapseSize,
      expandSize: resolvedInitialSize,
      minimumSize: MIN_PANEL_WIDTH,
      dimension: 'width',
      localStoragePersisterKey: 'reporterRightDrawerState',
      initialSize: MIN_PANEL_WIDTH,
      expandToLastSize: true,
    };

    const {
      isResizing,
      resizableCallbacks,
      componentSize,
      componentLastExpandedSize,
      openComponent: openRightMenu,
      toggleOpenComponent: toggleOpenRightMenu,
      sizes: drawerSizes,
      resizeListener: drawerSizeListener,
      showCollapseWarning,
    } = useResizableWithPersistence({
      ...sidebarResizeOptions,
      localStoragePersisterKey: 'reporterRightDrawerState',
    });

    const size = useMemo(() => ({ width: componentSize }), [componentSize]);

    const handleComponent = useMemo(
      () => ({
        left: (
          <ResizeHandle
            showCollapseWarning={showCollapseWarning}
            collapsed={!openRightMenu && !isResizing}
          />
        ),
      }),
      [isResizing, openRightMenu, showCollapseWarning]
    );

    useEffect(() => {
      if (drawerSizes?.height == null) {
        return;
      }

      onHeightChange?.(drawerSizes.height);

      setRightDrawer?.((state) => ({
        ...state,
        drawerHeight: drawerSizes.height,
      }));
    }, [drawerSizes?.height, onHeightChange, setRightDrawer]);

    useEffect(() => {
      setReporterPreferences((prev) => ({
        ...prev,
        sidebarSize: componentSize,
        sidebarLastExpandedSize: componentLastExpandedSize,
      }));
    }, [componentLastExpandedSize, componentSize, setReporterPreferences]);

    useEffect(() => {
      setReporterPreferences((prev) => ({
        ...prev,
        isSidebarOpen: openRightMenu,
      }));
    }, [openRightMenu, setReporterPreferences]);

    return (
      <Resizable
        size={size as Size}
        {...resizableCallbacks}
        maxWidth={maxWidth ?? MAX_PANEL_WIDTH}
        enable={resizableEnable}
        css={`
          display: flex;
          transition: width ${isResizing === false ? 0.2 : 0}s ease-in-out;
          background-color: ${Colors.gray3};
        `}
        handleComponent={handleComponent}
      >
        <button
          type="button"
          onClick={() => {
            logger.info(`[${resolvedLoggerLabel}] Right drawer toggle button clicked`, {
              value: !openRightMenu,
            });

            toggleOpenRightMenu();
          }}
          css={`
            padding: 2px 0px;
            outline: none;
            margin: 0;
            border: unset;
            color: ${Colors.white};
            position: absolute;
            top: 28px;
            cursor: pointer;
            z-index: 1;
            background-color: ${Colors.gray5};
            transition: background-color 0.2s ease-in-out;
            &:hover {
              background-color: ${Colors.gray6};
            }
            left: ${openRightMenu ? -15 : -25}px;
            ${openRightMenu
              ? 'border-radius: 15px;'
              : 'border-top-left-radius: 15px; border-bottom-left-radius: 15px;'}
            height: 24px;
            aspect-ratio: 1;
          `}
        >
          {openRightMenu ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </button>
        {drawerSizeListener}
        {drawerSizes?.height != null && <Container>{children}</Container>}
      </Resizable>
    );
  }
);

RightDrawerWrapper.displayName = 'RightDrawerWrapper';
